import * as React from "react";

import { format } from "date-fns";
import "./styles/price_calculator";
import "./styles/people_count_selector";
import t from "./locales";
import i18n from "../i18n";
import { sumPrices } from "./helpers";
import { calculateInsuranceFee } from "../utils/priceHelper";
import { priceWithCommas, isAuItem } from "../utils/commonHelper";
import { getLocale } from "../utils/pathHelper";
import Price from "../Shared/Price";
import { Field, FieldGroup, FieldGroupLabel } from "../Shared/Fields";
import SnowItemCalendar from "../Calendar/SnowItemCalendar";
import SnowItemCartButton from "../ShoppingCart/SnowItemCartButton";
import SnowItemLessonStock from "../Calendar/SnowItemLessonStock";
import {
	IPeople,
	IPrice,
	IPriceTable,
	IResort,
	ICurrency,
} from "../ShoppingCart/types";
import { validateSnowItemFunc } from "../ShoppingCart/validations";
import CouponInterface from "./CouponInterface";
import { ICoupon, IPeopleCount } from "./types";
import { formatDateWithDayOfWeek } from "../utils/dateHelper";
import { ExchangeRateButton } from "./ExchangeRateButton";
import {
	renderAnyDiscountAmount,
	renderEarlyBirdAmount,
} from "./common/HighLightBadge";

interface IProps {
	itemId: number;
	itemName: string;
	dateDuration: number;
	isRangeSelect: boolean;
	priceTable: IPriceTable;
	minDateTime: Date;
	maxParticipants?: number;
	limitedTo?: "adult" | "child" | "student" | "senior";
	noCart?: boolean;
	resort?: IResort;
	selectedDateList?: Date[];
	localCurrency?: ICurrency;
	isKanjiwari: boolean;
	necessaryParticipants: number;
	deposit: number;
	isCharge: boolean;
	forAxess: boolean;
	isPartnerEc?: boolean;
	isSeasonPass?: boolean;
	coupons: ICoupon[];
	selectedPeopleCount: IPeopleCount;
	studentLabelName?: string;
	middleStudentLabelName?: string;
	seniorLabelName?: string;
	merchantId?: string;
	typeLift?: boolean;
	isEarlyBird: boolean;
	anyDiscount: boolean;
	saleDateTo: Date;
	setProduct?: boolean;
}

interface IState {
	isCalendarShown: boolean;
	people: IPeople;
	selectedDateList: Date[];
	payableDateList: Date[];
	timeSchedule: string;
	isTimeScheduleShown: boolean;
	selectedCoupon: ICoupon | null;
}

const sumPriceEachDate = (
	priceTable: IPriceTable,
	payableDateList: Date[],
	people: IPeople,
): IPrice => {
	const prices: IPrice[] = [];
	for (let i = 0; i < payableDateList.length; i++) {
		const dateString = format(payableDateList[i], "yyyy-MM-dd");
		const targetPrice = priceTable[dateString];
		prices.push(targetPrice);
	}
	return sumPrices(prices, people);
};

function isPricePresentThroughAllDate(
	priceTable: IPriceTable,
	name: "adultPrice" | "childPrice" | "studentPrice" | "seniorPrice",
): boolean {
	return Object.values(priceTable).every(
		(price: IPrice) => price[name] != null || price.groupPrice != null,
	);
}

class SnowItemPriceCalculator extends React.Component<IProps, IState> {
	static defaultProps = {
		noCart: false,
	};

	constructor(props: IProps) {
		super(props);
		this.state = {
			isCalendarShown: false,
			people: this.props.selectedPeopleCount || {
				adultNum: this.props.isKanjiwari ? this.props.necessaryParticipants : 0,
				childNum: 0,
				studentNum: 0,
				seniorNum: 0,
			},
			selectedDateList: this.props.selectedDateList || [],
			payableDateList: this.props.selectedDateList || [],
			timeSchedule: null,
			isTimeScheduleShown: false,
			selectedCoupon: null,
		};

		this.handleCouponSelect = this.handleCouponSelect.bind(this);
	}

	handleNumberOfPeopleChange(name: string, increment: number) {
		this.setState((prevState) => {
			const updatedPeople = {
				...prevState.people,
				[name]: Math.max(0, prevState.people[name] + increment),
			};
			const totalParticipants =
				updatedPeople.adultNum +
				updatedPeople.childNum +
				updatedPeople.studentNum +
				updatedPeople.seniorNum;

			if (
				this.props.maxParticipants &&
				totalParticipants > this.props.maxParticipants
			) {
				alert(
					t.pleaseSelectValidNumberOfParticipants({
						max: this.props.maxParticipants,
					}),
				);
				return prevState;
			}

			return {
				...prevState,
				people: updatedPeople,
			};
		});
	}

	getLocaleByEnv() {
		// 本番環境の場合、localeをsubdomainから取得
		let locale = getLocale({ underscore: true });
		// localとstg環境で言語切り替えボタンを押したら、localeをユーザー選択した言語から取得
		if (
			process.env.RAILS_ENV === "development" ||
			process.env.RAILS_ENV === "staging"
		) {
			let buttonElement = document.querySelector("#lang-button .btn");
			locale = buttonElement.textContent.trim();
		}
		return locale;
	}

	handleDateListChange(
		selectedDateList: Date[],
		payableDateList: Date[],
	): void {
		const stockIntegration = this.props.resort!.stockIntegration;
		let { isTimeScheduleShown } = this.state;
		if (
			stockIntegration != null &&
			stockIntegration != "" &&
			selectedDateList.length > 0
		) {
			isTimeScheduleShown = !isTimeScheduleShown;
		}
		const timeSchedule = "";
		this.setState({
			selectedDateList,
			payableDateList,
			isTimeScheduleShown,
			timeSchedule,
		});
	}

	handleLessonScheduleSelect(timeSchedule: string): void {
		document.getElementById("cta-container").style.zIndex = "1";
		this.setState({
			timeSchedule,
			isTimeScheduleShown: false,
			isCalendarShown: false,
		});
	}

	handleLessonScheduleClose(): void {
		this.setState({ isTimeScheduleShown: false });
	}

	toggleCalendarModal(event: React.MouseEvent): void {
		event.preventDefault();
		const isCalendarShown = !this.state.isCalendarShown;
		this.setState({ isCalendarShown });
	}

	handleCouponSelect(selectedCoupon: ICoupon) {
		this.setState({ selectedCoupon });
	}

	renderDateInput() {
		const { selectedDateList } = this.state;
		const { isPartnerEc, dateDuration } = this.props;
		const firstDate = selectedDateList[0];
		const numberOfDate = selectedDateList.length;
		let inputString = "";
		if (numberOfDate === 0) {
			inputString = t.pleaseSelect();
		}
		if (numberOfDate === 1) {
			inputString = formatDateWithDayOfWeek(firstDate);
		}
		if (numberOfDate > 1) {
			const lastDate = selectedDateList[numberOfDate - 1];
			inputString = `${formatDateWithDayOfWeek(firstDate)} - ${formatDateWithDayOfWeek(lastDate)}`;
		}

		// 連日設定の場合（dateDuration > 1）はラベルの下に利用日を表示
		const isConsecutiveDays = dateDuration > 1;

		// 条件に応じてスタイルを変更
		const fieldGroupStyle: React.CSSProperties = isConsecutiveDays
			? { flexDirection: "column", alignItems: "stretch" }
			: {};
		const labelStyle: React.CSSProperties = isConsecutiveDays
			? { minWidth: "auto", marginBottom: "8px" }
			: { minWidth: 64 };
		const inputGroupStyle: React.CSSProperties = isConsecutiveDays
			? { width: "100%" }
			: {};
		const inputStyle: React.CSSProperties = isConsecutiveDays
			? { width: "100%" }
			: {};

		return (
			<div
				className={`price-calculator__section__field-group ${isPartnerEc ? "border-top-0" : ""}`}
				style={fieldGroupStyle}
			>
				<div
					className="price-calculator__section__field-group__label"
					style={labelStyle}
				>
					{t.dateOfUse()}
				</div>
				<div
					className="price-calculator__section__field-group__input"
					onClick={this.toggleCalendarModal.bind(this)}
					style={inputGroupStyle}
				>
					<input value={inputString} readOnly style={inputStyle} />
					<i className="fas fa-chevron-down"></i>
				</div>
			</div>
		);
	}

	ticketPrice() {
		const { priceTable, localCurrency } = this.props;
		const { payableDateList, people } = this.state;
		const priceData = sumPriceEachDate(priceTable, payableDateList, people);
		const {
			adultPrice,
			childPrice,
			groupPrice,
			studentPrice,
			seniorPrice,
			insuranceApplicable,
			maxParticipantsAtOnce,
			forGroup,
		} = priceData;

		// 保険込みレッスン商品の場合保険料を計算
		const totalPeopleCount =
			people.adultNum + people.childNum + people.studentNum + people.seniorNum;
		const insuranceFee = calculateInsuranceFee(
			insuranceApplicable,
			forGroup,
			maxParticipantsAtOnce,
			totalPeopleCount,
		);

		const price =
			adultPrice +
			childPrice +
			groupPrice +
			studentPrice +
			seniorPrice +
			insuranceFee;
		return price;
	}

	renderPriceSummary() {
		const priceData = sumPriceEachDate(
			this.props.priceTable,
			this.state.payableDateList,
			this.state.people,
		);
		const {
			adultPrice,
			childPrice,
			groupPrice,
			studentPrice,
			seniorPrice,
			adultFullPrice,
			childFullPrice,
			groupFullPrice,
			studentFullPrice,
			seniorFullPrice,
			insuranceApplicable,
			maxParticipantsAtOnce,
			forGroup,
		} = priceData;

		// 保険料を計算
		const totalPeopleCount =
			this.state.people.adultNum +
			this.state.people.childNum +
			this.state.people.studentNum +
			this.state.people.seniorNum;
		const insuranceFee = calculateInsuranceFee(
			insuranceApplicable,
			forGroup,
			maxParticipantsAtOnce,
			totalPeopleCount,
		);

		const price: number =
			adultPrice +
			childPrice +
			groupPrice +
			studentPrice +
			seniorPrice +
			insuranceFee;
		const fullPrice: number =
			adultFullPrice +
			childFullPrice +
			groupFullPrice +
			studentFullPrice +
			seniorFullPrice +
			insuranceFee;
		if (price === 0) {
			return null;
		}

		return (
			<Price
				price={price}
				fullPrice={fullPrice}
				showDiscount
				style={{ marginRight: "0.5em" }}
				localCurrency={this.props.localCurrency}
			/>
		);
	}

	renderCloseButton() {
		return (
			<div>
				<a
					href="#"
					className="price-calculator__button"
					onClick={this.toggleCalendarModal.bind(this)}
				>
					OK
				</a>
			</div>
		);
	}

	renderActionButton() {
		const {
			itemId,
			itemName,
			dateDuration,
			priceTable,
			resort,
			maxParticipants,
			deposit,
			isCharge,
			forAxess,
			merchantId,
			typeLift,
			isEarlyBird,
			saleDateTo,
		} = this.props;
		const { people, payableDateList, timeSchedule } = this.state;
		const validate = validateSnowItemFunc({
			itemId,
			people,
			payableDateList,
			resort,
			timeSchedule,
		});

		return (
			<SnowItemCartButton
				itemId={itemId}
				itemName={itemName}
				people={people}
				payableDateList={payableDateList}
				dateDuration={dateDuration}
				priceTable={priceTable}
				maxParticipants={maxParticipants}
				resort={resort}
				timeSchedule={timeSchedule}
				validate={validate}
				isKanjiwari={this.props.isKanjiwari}
				deposit={deposit}
				isCharge={isCharge}
				forAxess={forAxess}
				merchantId={merchantId}
				typeLift={typeLift}
				isEarlyBird={isEarlyBird}
				saleDateTo={saleDateTo}
				setProduct={this.props.setProduct}
				directCheckout={this.props.noCart}
			/>
		);
	}

	renderCalendarModal(isShown: boolean = false) {
		const { isRangeSelect, dateDuration, priceTable, minDateTime } = this.props;
		const { selectedDateList, payableDateList } = this.state;
		return (
			<div
				className={`price-calculator-modal ${isShown ? "active" : ""}`}
				onClick={this.toggleCalendarModal.bind(this)}
			>
				<div
					className="price-calculator-modal__inner"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<SnowItemCalendar
						minDate={minDateTime}
						isRangeSelect={isRangeSelect}
						dateDuration={dateDuration}
						priceTable={priceTable}
						selectedDateList={selectedDateList}
						payableDateList={payableDateList}
						handleDateListChange={this.handleDateListChange.bind(this)}
						localCurrency={this.props.localCurrency}
					/>
					<div className="price-calculator-modal__footer">
						{this.renderPriceSummary()}
						{this.renderCloseButton()}
					</div>
				</div>
			</div>
		);
	}

	renderPeopleCountSelector() {
		const locale = this.getLocaleByEnv();
		const {
			priceTable,
			limitedTo,
			localCurrency,
			isPartnerEc,
			studentLabelName,
			middleStudentLabelName,
			seniorLabelName,
		} = this.props;
		const { rate } = localCurrency;
		const peopleTypes = [
			{ name: "adult" },
			{ name: "child" },
			{ name: "student" },
			{ name: "senior" },
		];
		const timeSchedule = this.state.timeSchedule || "";

		const selectedDate =
			this.state.payableDateList.length > 0
				? format(this.state.payableDateList[0], "yyyy-MM-dd")
				: "";
		const groupPrice = priceTable[selectedDate]?.groupPrice;
		const groupFullPrice = priceTable[selectedDate]?.groupFullPrice;
		// 処理済みの価格データを取得（保険情報を含む）
		const priceData = sumPriceEachDate(
			priceTable,
			this.state.payableDateList,
			this.state.people,
		);
		let { insuranceApplicable, maxParticipantsAtOnce, forGroup } = priceData;

		const isGroupPriceAvailable = groupPrice != null;

		const localizePrice = (price) => {
			return price * rate;
		};

		const totalPeople = () => {
			return peopleTypes.reduce(
				(acc, type) => acc + this.state.people[`${type.name}Num`],
				0,
			);
		};

		// 保険込みレッスンの保険料計算
		const totalPeopleCount = totalPeople();
		const insuranceFee = calculateInsuranceFee(
			insuranceApplicable,
			forGroup,
			maxParticipantsAtOnce,
			totalPeopleCount,
		);

		const groupDiscountRate =
			groupFullPrice > 0
				? Math.floor(((groupFullPrice - groupPrice) / groupFullPrice) * 100)
				: undefined;

		return (
			<div>
				{isGroupPriceAvailable && (
					<div className="people-count-selector mb-3">
						<span className="font-weight-bold">{t.groupPrice()}</span>
						<div className="people-count-selector__price-info__price">
							<div className="d-flex flex-column justify-content-between align-items-center">
								{groupDiscountRate > 0 && (
									<div className="d-flex align-items-center justify-content-between w-100">
										<div className="people-count-selector__price-info__discount-rate">
											<div className="people-count-selector__price-info__discount-rate__text">
												{i18n.t("base.percent_off", {
													percent: groupDiscountRate,
												})}
											</div>
										</div>
									</div>
								)}
								<div className="d-flex justify-content-between align-items-center">
									{groupFullPrice && (
										<span className="people-count-selector__price-info__price--full">
											{localCurrency.code === "JPY"
												? `${priceWithCommas(groupFullPrice + insuranceFee)}円`
												: `${localCurrency.code} ${priceWithCommas(localizePrice(groupFullPrice + insuranceFee))}`}
										</span>
									)}
									<span className="people-count-selector__price-info__price--discounted">
										{localCurrency.code === "JPY"
											? `${priceWithCommas(groupPrice + insuranceFee)}円`
											: `${localCurrency.code} ${priceWithCommas(localizePrice(groupPrice + insuranceFee))}`}
									</span>
								</div>
							</div>
						</div>
					</div>
				)}
				{peopleTypes.map((type) => {
					if (this.props.setProduct && type.name !== "adult") {
						return null;
					}
					const priceName = `${type.name}Price` as
						| "adultPrice"
						| "childPrice"
						| "studentPrice"
						| "seniorPrice";
					const numName = `${type.name}Num` as
						| "adultNum"
						| "childNum"
						| "studentNum"
						| "seniorNum";
					let discountedPrice = priceTable[selectedDate]?.[priceName];
					let fullPrice = priceTable[selectedDate]?.[`${type.name}FullPrice`];

					if (
						(discountedPrice == null && !isGroupPriceAvailable) ||
						(limitedTo !== null && limitedTo !== type.name)
					) {
						return null;
					}

					if (!isPricePresentThroughAllDate(priceTable, priceName)) {
						return null;
					}

					if (discountedPrice === fullPrice) {
						fullPrice = null;
					}

					// 保険が適用される場合、価格に保険料を追加（1人分の保険料）
					const insuranceFeePerPerson = calculateInsuranceFee(
						insuranceApplicable,
						forGroup,
						maxParticipantsAtOnce,
						1,
					);
					const displayDiscountedPrice =
						discountedPrice + insuranceFeePerPerson;
					const displayFullPrice = fullPrice
						? fullPrice + insuranceFeePerPerson
						: fullPrice;

					let localizedDiscountedPrice;
					let localizedFullPrice;

					if (localCurrency.code === "JPY") {
						localizedDiscountedPrice = `${priceWithCommas(displayDiscountedPrice)}円`;
						if (displayFullPrice) {
							localizedFullPrice = `${priceWithCommas(displayFullPrice)}円`;
						}
					} else {
						localizedDiscountedPrice = `${localCurrency.code} ${priceWithCommas(localizePrice(displayDiscountedPrice))}`;
						if (displayFullPrice) {
							localizedFullPrice = `${localCurrency.code} ${priceWithCommas(localizePrice(displayFullPrice))}`;
						}
					}

					const getLabelName = () => {
						if (numName === "adultNum" && this.props.setProduct) {
							return t.setProductNum();
						}
						if (numName === "studentNum") {
							if (middleStudentLabelName) {
								return t[middleStudentLabelName]();
							}
							if (studentLabelName) {
								return t[studentLabelName]();
							}
							return t[numName]();
						}
						if (numName === "seniorNum") {
							if (seniorLabelName) {
								return t[seniorLabelName]();
							}
							return t[numName]();
						}
						return t[numName]();
					};

					const discountRate =
						fullPrice > 0
							? Math.floor(((fullPrice - discountedPrice) / fullPrice) * 100)
							: undefined;

					return (
						<div key={numName} className="people-count-selector mb-3">
							<div className="d-flex flex-column justify-content-between align-items-center">
								{discountRate > 0 && (
									<div className="d-flex align-items-center justify-content-between w-100">
										<div className="people-count-selector__price-info__discount-rate">
											<div className="people-count-selector__price-info__discount-rate__text">
												{i18n.t("base.percent_off", { percent: discountRate })}
											</div>
										</div>
									</div>
								)}
								<div className="d-flex justify-content-between align-items-center">
									{(numName == "adultNum" ||
										numName == "childNum" ||
										numName == "studentNum" ||
										numName == "seniorNum") && (
										<span className="people-count-selector__price-info__num-name">
											{getLabelName()}
										</span>
									)}
									{!isGroupPriceAvailable && (
										<div className="people-count-selector__price-info__price">
											{fullPrice && (
												<span className="people-count-selector__price-info__price--full">
													{localizedFullPrice}
												</span>
											)}
											<span className="people-count-selector__price-info__price--discounted">
												{localizedDiscountedPrice}
											</span>
										</div>
									)}
								</div>
							</div>
							<div className="people-count-selector__counter">
								<button
									type="button"
									className={`people-count-selector__counter_button people-count-selector__counter_button--decrement ${
										this.state.people[numName] === 0 ? "muted" : ""
									}`}
									onClick={() => this.handleNumberOfPeopleChange(numName, -1)}
								>
									<i className="fa fa-minus fa-sm"></i>
								</button>
								<span
									className={`people-count-selector__counter__value ${this.state.people[numName] > 0 ? "font-weight-bold" : ""}`}
								>
									{this.state.people[numName]}
								</span>
								<button
									type="button"
									className="people-count-selector__counter_button people-count-selector__counter_button--increment"
									onClick={() => this.handleNumberOfPeopleChange(numName, 1)}
									disabled={totalPeople() >= 10}
								>
									<i className="fa fa-plus fa-sm"></i>
								</button>
							</div>
							{isPartnerEc && (
								<>
									<input
										type="hidden"
										name={`order_group[orders_attributes][0][${type.name}_num]`}
										value={this.state.people[numName]}
									/>
									<input
										type="hidden"
										name="order_group[orders_attributes][0][scheduled_date]"
										value={selectedDate}
									/>
									<input
										type="hidden"
										name="order_group[orders_attributes][0][time_schedule]"
										value={timeSchedule}
									/>
								</>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	renderAuMemo() {
		const adultThree = [5776, 5780, 5782, 5790, 5795, 5799, 5803];
		const adultFour = [5777, 5791, 5800];
		const childOne = [5778, 5780, 5783, 5786, 5792, 5796, 5801, 5803];
		const childTwo = [5779, 5793, 5802];
		const url = document.location.pathname;
		const itemId = Number(url.split("/").pop());
		const auAdult = adultThree.includes(itemId)
			? 3
			: adultFour.includes(itemId)
				? 4
				: 2;
		const auChild = childOne.includes(itemId)
			? 1
			: childTwo.includes(itemId)
				? 2
				: 0;
		return `This plan is for adult: ${auAdult} child: ${auChild}`;
	}

	renderParticipantsNumber() {
		return (
			<FieldGroup key={"adultNum"} style={{ width: "48%" }}>
				<FieldGroupLabel>{t["adultNum"]()}</FieldGroupLabel>
				<Field noLeftBorder style={{ lineHeight: "40px", padding: "0 1em" }}>
					{this.props.necessaryParticipants}
				</Field>
			</FieldGroup>
		);
	}

	renderLessonScheduleSelect() {
		const stockIntegration = this.props.resort!.stockIntegration;
		if (stockIntegration == null) {
			return null;
		}

		const { selectedDateList } = this.state;
		const numberOfDate = selectedDateList.length;
		if (numberOfDate === 0) {
			return null;
		}

		const { timeSchedule } = this.state;
		let inputString = "";
		if (timeSchedule != null) {
			inputString = timeSchedule;
		}
		return (
			<FieldGroup>
				<FieldGroupLabel style={{ minWidth: 64 }}>
					{t.timeSchedule()}
				</FieldGroupLabel>
				<Field noLeftBorder>
					<input
						value={inputString}
						readOnly
						onClick={this.toggleLessonScheduleModal.bind(this)}
					/>
				</Field>
			</FieldGroup>
		);
	}

	toggleLessonScheduleModal(event: React.MouseEvent): void {
		event.preventDefault();
		const { selectedDateList } = this.state;
		if (selectedDateList.length == 0) {
			return;
		}

		const isTimeScheduleShown = !this.state.isTimeScheduleShown;
		const isCalendarShown = this.state.isCalendarShown;
		document.getElementById("cta-container").style.zIndex =
			`${isTimeScheduleShown || isCalendarShown ? 1049 : 1}`;
		this.setState({ isTimeScheduleShown });
	}

	renderLessonStockModal(isShown: boolean = false) {
		const { itemId, resort } = this.props;
		const stockIntegration = resort!.stockIntegration;
		const { selectedDateList } = this.state;
		return (
			<div
				className={`price-calculator-modal ${isShown ? "active" : ""}`}
				onClick={this.toggleLessonScheduleModal.bind(this)}
			>
				<div
					className="price-calculator-modal__inner"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<SnowItemLessonStock
						stockIntegration={stockIntegration}
						selectedDateList={selectedDateList}
						snowItemId={itemId}
						isTimeScheduleShown={isShown}
						handleLessonScheduleSelect={this.handleLessonScheduleSelect.bind(
							this,
						)}
						handleLessonScheduleClose={this.handleLessonScheduleClose.bind(
							this,
						)}
					/>
				</div>
			</div>
		);
	}

	renderDepositAmount() {
		const { deposit, localCurrency } = this.props;
		const { rate } = localCurrency;
		const locale = this.getLocaleByEnv();
		let localizedDeposit: any;

		localizedDeposit =
			priceWithCommas(deposit * rate).toString() + localCurrency.code;

		return (
			<div className="mt-2">
				<div className="people-count-selector__price-info">
					<span className="mr-1">{t["icCardDeposit"]()}</span>
					<span className="people-count-selector__price-info__price people-count-selector__price-info__price--deposit">
						{localCurrency.code === "JPY" ? `${deposit}円` : `JPY ${deposit}`}
					</span>
					{localCurrency.code !== "JPY" && (
						<span className="text-sm text-muted ml-1">
							({localizedDeposit})
						</span>
					)}
				</div>
				<div className="text-sm">{t["icCardDepositNote"]()}</div>
			</div>
		);
	}

	getTotalHeadcount() {
		const { people } = this.state;

		return Object.values(people).reduce((total, num) => total + num, 0);
	}

	render() {
		const { isCalendarShown, isTimeScheduleShown, selectedCoupon } = this.state;
		const { coupons, isPartnerEc, localCurrency, deposit } = this.props;
		const ticketPrice = this.ticketPrice();
		let discount = 0;

		const totalHeadcount = this.getTotalHeadcount();
		const totalDeposit = deposit * totalHeadcount;

		if (selectedCoupon) {
			if (selectedCoupon.discountRate != null) {
				discount = ticketPrice * selectedCoupon.discountRate;
			} else if (selectedCoupon.discountAmount != null) {
				discount = selectedCoupon.discountAmount;
			}
		}

		const finalPrice = ticketPrice + totalDeposit - discount;
		const locale = this.getLocaleByEnv();

		let localizedFinalPrice: string | number = finalPrice;
		let localCurrencyCode = "JPY";

		if (locale !== "ja" && localCurrency) {
			const { rate, code } = localCurrency;
			localizedFinalPrice = finalPrice * rate;
			localCurrencyCode = code;
			localizedFinalPrice =
				localCurrencyCode === "USD"
					? localizedFinalPrice.toFixed(2)
					: Math.round(localizedFinalPrice).toString();
		}

		const selectedDate =
			this.state.payableDateList.length > 0
				? format(this.state.payableDateList[0], "yyyy-MM-dd")
				: "";
		const isSelectedPriceDiscounted =
			this.state.payableDateList.length > 0 &&
			(this.props.priceTable[selectedDate]?.groupFullPrice !==
				this.props.priceTable[selectedDate]?.groupPrice ||
				this.props.priceTable[selectedDate]?.adultFullPrice !==
					this.props.priceTable[selectedDate]?.adultPrice ||
				this.props.priceTable[selectedDate]?.childFullPrice !==
					this.props.priceTable[selectedDate]?.childPrice ||
				this.props.priceTable[selectedDate]?.studentFullPrice !==
					this.props.priceTable[selectedDate]?.studentPrice ||
				this.props.priceTable[selectedDate]?.seniorFullPrice !==
					this.props.priceTable[selectedDate]?.seniorPrice);

		return (
			<div>
				{isAuItem() && (
					<div className="price-calculator__auMemo">{this.renderAuMemo()}</div>
				)}

				<div
					className={`price-calculator__section ${this.props.isPartnerEc ? "card bg-snow-white my-4 p-3 rounded-0" : ""}`}
				>
					{this.props.isPartnerEc && !this.props.isSeasonPass && (
						<p className="font-weight-bold">{t.selectNumOfPeopleAndDate()}</p>
					)}
					{this.props.isPartnerEc && this.props.isSeasonPass && (
						<p className="font-weight-bold">{`${i18n.t("snow_items.order_form.select_pass_type")}`}</p>
					)}
					{this.renderDateInput()}
					{this.renderLessonScheduleSelect()}
					{this.props.deposit > 0 && this.renderDepositAmount()}
					{this.state.payableDateList.length > 0 && (
						<div className="my-3">
							{locale !== "ja" && <ExchangeRateButton />}
						</div>
					)}
					{!this.props.isPartnerEc && (
						<>
							{this.state.payableDateList.length > 0 &&
								this.props.isEarlyBird &&
								renderEarlyBirdAmount(this.props.saleDateTo)}
							{this.state.payableDateList.length > 0 &&
								isSelectedPriceDiscounted &&
								!this.props.isEarlyBird &&
								this.props.anyDiscount &&
								renderAnyDiscountAmount()}
						</>
					)}
					{(!this.props.isCharge || this.props.limitedTo == null) &&
						this.renderPeopleCountSelector()}
				</div>

				{/* SEE: https://github.com/wamazing/wamazing-snow/issues/14210
					{this.props.isPartnerEc && (
						<div className="price-calculator__coupon card bg-snow-white my-4 p-3 rounded-0">
							<CouponInterface
								coupons={coupons}
								isPartnerEc={isPartnerEc}
								onSelectCoupon={this.handleCouponSelect}
							/>
						</div>
					)}
				*/}

				{this.props.isPartnerEc && deposit > 0 && totalHeadcount > 0 && (
					<div className="col-lg-5 text-center mx-auto">
						<div className="d-flex align-items-center justify-content-between">
							<p>{`${t.icCardDeposit()} × ${totalHeadcount}`}</p>
							<p className="text-primary">
								{localCurrency.code === "JPY"
									? `${totalDeposit} 円`
									: `JPY ${totalDeposit}`}
							</p>
						</div>
					</div>
				)}

				{this.props.isPartnerEc && selectedCoupon && (
					<div className="col-lg-5 text-center mx-auto">
						<div className="d-flex align-items-center justify-content-between">
							<p>{t.couponDiscount()}</p>
							<p className="text-primary">
								{localCurrency.code === "JPY"
									? `-${discount} 円`
									: `-JPY ${discount}`}
							</p>
						</div>
					</div>
				)}

				{this.props.isPartnerEc && (
					<div className="col-lg-5 text-center mx-auto">
						<div className="d-flex justify-content-between">
							<p className="font-weight-bold">{t.paymentTotal()}</p>
							<div className="d-flex align-items-center justify-content-between text-right">
								{locale !== "ja" && (
									<p className="text-muted text-sm mr-4">
										JPY {priceWithCommas(finalPrice)}
									</p>
								)}
								<p className="font-weight-bold text-primary">
									{localCurrency && localCurrency.code === "JPY"
										? `${priceWithCommas(finalPrice)} 円`
										: `${localCurrencyCode} ${localizedFinalPrice}`}
								</p>
								<input
									type="hidden"
									id="payment_amount"
									value={`${priceWithCommas(finalPrice)}`}
								/>
							</div>
						</div>
					</div>
				)}

				{!this.props.isPartnerEc && (
					<div className="price-calculator__cta">
						{this.renderActionButton()}
					</div>
				)}
				{this.renderCalendarModal(isCalendarShown)}
				{this.renderLessonStockModal(isTimeScheduleShown)}
			</div>
		);
	}
}

export default SnowItemPriceCalculator;
